import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useDiscounts } from '../DiscountsProvider';
import { Discount } from '../DiscountsProviderTypes';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { findCouponForKeyValue, validatePayload } from '../discountsUtils';
import { useTranslation } from '@shared/core';
import { useManagedSearchParam } from '@shared/hooks/useManagedSearchParam';
import { QuickTranslate } from '@shared/core/i18n/useTranslation';
import { getConfig } from '@config';

const config = getConfig();

/**
 * Enum for hotel discount-related query string parameters.
 */
export enum JoyHotelQueryStringParameters {
  joyHotelDiscountRule = 'joyHotelDiscountRule',
  joyHotelDiscountCode = 'joyHotelDiscountCode',
}

export type ExtendedDiscount = Discount &
  Readonly<{
    label: React.ReactNode;
  }>;

type HotelDiscountsContextType = Readonly<{
  hotelDiscounts: ReadonlyArray<ExtendedDiscount>;
  /**
   * The hotel discount selected via the query string parameter (joyHotelDiscountCode), if any.
   */
  joyHotelDiscount: ExtendedDiscount | undefined;
  setJoyHotelDiscountByRule: (rule: string) => void;
}>;

const HotelsDiscountsContext = createContext<HotelDiscountsContextType | undefined>(undefined);

/**
 * Helper to generate a translated label for a discount.
 * @param discount - Discount object
 * @param tq - QuickTranslate function
 * @returns Translated label string
 */
const labelForDiscount = (discount: Discount, tq: QuickTranslate) => (
  <>
    <span
      role="img"
      aria-hidden="true"
    >
      🏷️
    </span>{' '}
    {tq`${discount.percent}% Off`}
  </>
);
/**
 * Provider component for hotel discounts context.
 * Wrap your component tree with this to enable hotel discount features.
 *
 * @param children - React children nodes
 * @returns JSX.Element
 */
export const HotelsDiscountsProvider = ({ children }: Readonly<{ children: React.ReactNode }>) => {
  const { tq } = useTranslation('stays');
  const context = useDiscounts();
  const [couponsFlag, , , payload] = useIsFeatureEnabled('travelHotelCoupons');
  const [searchParamCode, setSearchParamCode] = useManagedSearchParam(JoyHotelQueryStringParameters.joyHotelDiscountCode);
  const [isTripProfitOptedOut, isTripProfitOptedOutLoading] = useIsFeatureEnabled({ consentKey: 'websiteOptOutTripProfit' });

  // store coupon in a list managed by DiscountsProvider
  const registerDiscountByRule = useCallback(
    (rule: string) => {
      const discount = findCouponForKeyValue('rule', rule, payload);
      if (discount) {
        context?.registerDiscount(discount);
      }
    },
    [context, payload],
  );

  // set the JoyHotelQueryStringParameters.joyHotelDiscountCode code and register the discount
  const setJoyHotelDiscountByRule = useCallback(
    (rule: string) => {
      const discount = findCouponForKeyValue('rule', rule, payload);
      if (discount) {
        setSearchParamCode(discount.code);
        // register the discount in list of discounts
        registerDiscountByRule(rule);
      }
    },
    [setSearchParamCode, payload, registerDiscountByRule],
  );

  // watcher on searchParamCode
  useEffect(() => {
    if (!context || !couponsFlag || !searchParamCode) {
      return;
    }
    const codeInfo = findCouponForKeyValue('code', searchParamCode, payload);
    if (!codeInfo) {
      return;
    }
    registerDiscountByRule(codeInfo.rule);
  }, [searchParamCode, context, couponsFlag, payload, registerDiscountByRule]);

  // extend the discount with additional information (label)
  const extendDiscount = useCallback(
    (discount: Discount): ExtendedDiscount => {
      const label = labelForDiscount(discount, tq);
      return {
        ...discount,
        label,
      };
    },
    [tq],
  );

  const hotelDiscounts = useMemo((): ReadonlyArray<ExtendedDiscount> => {
    const discounts = context?.discounts ?? [];
    return discounts.filter(discount => discount.category === 'hotels').map(extendDiscount);
  }, [context?.discounts, extendDiscount]);

  /**
   * Get the JOY hotel discount based on the search parameter code.
   * Returns undefined if:
   * - The user has opted out of TripProfit (websiteOptOutTripProfit = true)
   * - The consent is still loading (to avoid showing discount prematurely)
   */
  const joyHotelDiscount = useMemo((): ExtendedDiscount | undefined => {
    // Don't provide discount code if user has opted out OR consent is still loading
    // Loading check prevents race condition where discount briefly shows before consent loads
    if (isTripProfitOptedOut || isTripProfitOptedOutLoading) {
      return undefined;
    }

    const discount = hotelDiscounts.find(discount => discount.code === searchParamCode);
    return discount;
  }, [searchParamCode, hotelDiscounts, isTripProfitOptedOut, isTripProfitOptedOutLoading]);

  if (!context) {
    return <>{children}</>;
  }
  return (
    <HotelsDiscountsContext.Provider
      value={{
        hotelDiscounts,
        joyHotelDiscount,
        setJoyHotelDiscountByRule,
      }}
    >
      {children}
    </HotelsDiscountsContext.Provider>
  );
};

/**
 * Hook to access hotel discounts context.
 * Must be used within a HotelsDiscountsProvider.
 *
 * @returns HotelDiscountsContextType
 * @throws Error if used outside HotelsDiscountsProvider
 */
/**
 * Hook to access the currently selected JOY hotel discount directly.
 * NOTE: This replaces the previous `useHotelDiscounts` which returned the entire context object.
 * If you need additional data (hotelDiscounts list or setters) consider creating a focused hook
 * or refactoring the usage rather than re‑introducing broad context leakage.
 *
 * @returns The selected ExtendedDiscount (Discount with label) or undefined.
 */
export const useHotelDiscount = (): ExtendedDiscount | undefined => {
  const context = useContext(HotelsDiscountsContext);
  if (!context) {
    // If no HotelsDiscountsProvider is in the tree, intentionally return undefined
    // (do not throw), treating as "no discount" for consumers. This is distinct from
    // the case where a provider is present but no discount is selected. This hook is
    // composed into widely used hooks, so failing silently avoids breaking consumers.
    return undefined;
  }
  return context.joyHotelDiscount;
};

/**
 * Returns the setter function for selecting a JOY hotel discount by rule, or `undefined` if the provider is absent.
 *
 * Contract note: Earlier versions threw when the provider was missing. Returning `undefined` is a permissive
 * fallback that avoids hard runtime failures but can mask incorrect tree composition. Call sites MUST
 * defensively handle an `undefined` return.
 *
 */
export const useSetJoyHotelDiscountByRule = (): HotelDiscountsContextType['setJoyHotelDiscountByRule'] | undefined => {
  const context = useContext(HotelsDiscountsContext);
  if (!context) {
    if (config.environment === 'dev') {
      console.warn('useSetJoyHotelDiscountByRule was called outside of a HotelsDiscountsProvider. This may indicate unintended usage and could make debugging harder.');
    }
    return undefined;
  }
  return context.setJoyHotelDiscountByRule;
};

// (Temporary) If any remaining internal modules still import useHotelDiscounts, they will break.
// Requirement explicitly states to rename; we intentionally remove the old export.

/**
 * Hook to retrieve information about a hotel discount coupon code.
 *
 * @param code - Discount code to look up.
 * @returns Discount object if found and feature is enabled, otherwise undefined.
 */
export const useHotelCouponCode = (code?: string): Discount | undefined => {
  const [isEnabled, , , payload] = useIsFeatureEnabled('travelHotelCoupons');
  if (!isEnabled || !code) {
    return undefined;
  }
  return validatePayload(payload) ? payload.find((item: Discount) => item.code === code) : undefined;
};
